/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';

/**
 * Full-screen splash loading for the agency dashboard.
 * Shows brand logo + agency name, then slides up smoothly on exit.
 */

interface DashboardSkeletonProps {
  agencyName?: string;
  leaving?: boolean;
  onExitComplete?: () => void;
}

const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({ agencyName, leaving, onExitComplete }) => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (leaving && onExitComplete) {
      const timer = setTimeout(onExitComplete, 600);
      return () => clearTimeout(timer);
    }
  }, [leaving, onExitComplete]);

  return createPortal(
    <div
      role="status"
      aria-label={t('a11y.loadingDashboard')}
      data-testid="skeleton-splash"
      className="flex items-center justify-center"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingInline: 'max(24px, env(safe-area-inset-left), env(safe-area-inset-right))',
        background: 'var(--surface-secondary, #faf7f4)',
        zIndex: 9999,
        opacity: leaving ? 0 : 1,
        transform: leaving ? 'translateY(-40px)' : 'translateY(0)',
        transition: 'opacity 0.5s ease, transform 0.55s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      <div
        className="flex flex-col items-center gap-6 sm:gap-8"
        style={{
          width: '100%',
          maxWidth: 480,
          opacity: visible ? 1 : 0,
          transform: `translateY(${visible ? 0 : 16}px)`,
          transition: 'opacity 0.6s ease, transform 0.8s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <HousienBrandLogo variant="full" height={96} />

        {agencyName && (
          <div
            className="flex flex-col items-center gap-2"
            style={{
              width: '100%',
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.8s ease 0.4s',
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: 'var(--text-secondary)',
                letterSpacing: '0.02em',
              }}
            >
              {t('loading.enteringInto')}
            </span>
            <span
              style={{
                fontSize: 'clamp(18px, 5vw, 26px)',
                fontWeight: 600,
                lineHeight: 1.25,
                color: 'var(--seed-base)',
                letterSpacing: '-0.01em',
                textAlign: 'center',
                maxWidth: '100%',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {agencyName}
            </span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default DashboardSkeleton;
